// src/cockpit4/components/SOPLibrary.tsx
// ============================================
// 工作流中心 · P1-功能4：企业SOP库
//
// 功能：
//   1. SOP列表（按部门/类别分组，搜索）
//   2. 创建/编辑SOP（Markdown编辑器，支持预览）
//   3. 版本管理（保存新版本，含changelog，可回滚）
//   4. 执行率统计（每个SOP的执行次数/成功率/平均耗时）
//   5. 优化建议（基于执行率自动生成，如成功率<80%建议优化）
//   6. 培训考核（培训材料 + 考核题目）
//   7. 关联工作流模板（选择已有的工作流模板关联）
//   8. 从Obsidian导入（检索Obsidian知识库中的SOP文档）
//
// 数据来源：
//   - SOP 文档：localStorage（key: xeaos_workflow_sops），用户录入持久化，无数据时显示"暂无SOP / 点击创建"
//   - Obsidian 知识库：RuntimeManager.query('knowledge', { type: 'search', query }) 检索
//   - 工作流模板：localStorage（key: xeaos_workflow_templates）读取，供关联选择
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Search,
  RefreshCw,
  Loader2,
  Plus,
  X,
  CheckCircle2,
  Trash2,
  Save,
  Filter,
  GitBranch,
  History,
  TrendingUp,
  AlertTriangle,
  Megaphone,
  Handshake,
  Activity,
  Users,
  Wallet,
  Cpu,
  Building2,
  FileText,
  Clock,
  Target,
  GraduationCap,
  Lightbulb,
  Database,
  RotateCcw,
  Eye,
  Pencil,
  Sparkles,
  Link2,
  Unlink,
  ClipboardList,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import SimpleMarkdownRenderer from "./SimpleMarkdownRenderer";

// ============================================
// 类型定义
// ============================================

type SOPCategory = 'marketing' | 'sales' | 'operations' | 'hr' | 'finance' | 'tech' | 'admin';
type SOPStatus = 'draft' | 'published' | 'archived';

interface SOPVersion {
  version: string;
  content: string;
  changelog: string;
  updatedAt: string;
  updatedBy: string;
}

interface SOP {
  id: string;
  title: string;
  category: SOPCategory;
  department: string;
  status: SOPStatus;
  content: string;          // SOP内容（Markdown）
  versions: SOPVersion[];   // 版本历史
  // 执行统计
  executionCount: number;
  successRate: number;      // 0-100
  avgDuration: number;      // 平均执行耗时（分钟）
  // 关联工作流
  linkedWorkflowTemplateId?: string;
  // 优化建议
  optimizationSuggestions?: string[];
  // 培训考核
  trainingMaterials?: string[];
  assessmentQuestions?: { question: string; answer: string }[];
  createdAt: string;
  updatedAt: string;
}

/** 工作流模板精简信息（用于关联选择） */
interface WorkflowTemplateOption {
  id: string;
  name: string;
  category?: string;
}

/** Obsidian 检索结果项 */
interface ObsidianItem {
  title: string;
  content: string;
  path?: string;
}

// ============================================
// 常量
// ============================================

/** SOP 持久化 localStorage key */
const SOP_STORAGE_KEY = "xeaos_workflow_sops";

/** 工作流模板 localStorage key（与 WorkflowTemplateLibrary 共用） */
const WORKFLOW_TEMPLATES_STORAGE_KEY = "xeaos_workflow_templates";

/** 类别元信息（标签 / 颜色 / 图标） */
const CATEGORY_META: Record<
  SOPCategory,
  { label: string; color: string; icon: typeof Megaphone }
> = {
  marketing: { label: "营销", color: "#F472B6", icon: Megaphone },
  sales: { label: "销售", color: "#34D399", icon: Handshake },
  operations: { label: "运营", color: "#38BDF8", icon: Activity },
  hr: { label: "人事", color: "#FBBF24", icon: Users },
  finance: { label: "财务", color: "#A78BFA", icon: Wallet },
  tech: { label: "技术", color: "#22D3EE", icon: Cpu },
  admin: { label: "行政", color: "#94A3B8", icon: Building2 },
};

/** 类别筛选选项（含"全部"） */
const CATEGORY_FILTERS: { id: "all" | SOPCategory; label: string }[] = [
  { id: "all", label: "全部" },
  { id: "marketing", label: "营销" },
  { id: "sales", label: "销售" },
  { id: "operations", label: "运营" },
  { id: "hr", label: "人事" },
  { id: "finance", label: "财务" },
  { id: "tech", label: "技术" },
  { id: "admin", label: "行政" },
];

/** 状态元信息 */
const STATUS_META: Record<SOPStatus, { label: string; color: string }> = {
  draft: { label: "草稿", color: "#94A3B8" },
  published: { label: "已发布", color: "#34D399" },
  archived: { label: "已归档", color: "#64748B" },
};

/** 类别下拉选项 */
const CATEGORY_OPTIONS = Object.entries(CATEGORY_META).map(([k, v]) => ({
  id: k as SOPCategory,
  label: v.label,
}));

/** 状态下拉选项 */
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([k, v]) => ({
  id: k as SOPStatus,
  label: v.label,
}));

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 安全读取 SOP 列表 */
function loadSops(): SOP[] {
  try {
    const raw = localStorage.getItem(SOP_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SOP[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 SOP 列表 */
function saveSops(sops: SOP[]): void {
  try {
    localStorage.setItem(SOP_STORAGE_KEY, JSON.stringify(sops));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 读取工作流模板列表（localStorage，与 WorkflowTemplateLibrary 共用） */
function loadWorkflowTemplateOptions(): WorkflowTemplateOption[] {
  try {
    const raw = localStorage.getItem(WORKFLOW_TEMPLATES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((t: any) => ({
      id: String(t.id ?? ""),
      name: String(t.name ?? "未命名模板"),
      category: t.category,
    })).filter((t: WorkflowTemplateOption) => t.id);
  } catch {
    return [];
  }
}

/** 格式化时间为可读字符串 */
function formatDateTime(iso: string): string {
  try {
    return iso.slice(0, 16).replace("T", " ");
  } catch {
    return iso;
  }
}

/** 版本号自增（patch +1，如 1.0.0 → 1.0.1） */
function bumpVersion(current: string | undefined): string {
  if (!current) return "1.0.0";
  const parts = current.split(".").map((p) => parseInt(p, 10));
  while (parts.length < 3) parts.push(0);
  if (parts.some((n) => Number.isNaN(n))) return "1.0.0";
  parts[2] += 1;
  return parts.join(".");
}

/**
 * 基于执行统计自动生成优化建议
 * 规则：
 *   - 成功率 < 60%  → 严重偏低，建议暂停并重写
 *   - 成功率 < 80%  → 偏低，建议复盘失败案例
 *   - 平均耗时 > 60 分钟 → 建议拆分步骤或并行化
 *   - 已发布但执行次数为 0 → 建议推广培训
 *   - 成功率 ≥ 95% 且执行次数 ≥ 10 → 可作为标杆SOP推广
 *   - 版本数 > 5 → 迭代频繁，建议固化流程
 */
function generateOptimizationSuggestions(sop: SOP): string[] {
  const suggestions: string[] = [];
  const sr = sop.successRate ?? 0;
  const ec = sop.executionCount ?? 0;
  const ad = sop.avgDuration ?? 0;
  const vn = sop.versions?.length ?? 0;

  if (sr < 60) {
    suggestions.push(`成功率严重偏低（${sr.toFixed(0)}%），建议立即暂停执行并重写关键步骤`);
  } else if (sr < 80) {
    suggestions.push(`成功率偏低（${sr.toFixed(0)}%），建议复盘失败案例并优化薄弱环节`);
  }

  if (ad > 60) {
    suggestions.push(`平均耗时较长（${ad.toFixed(0)} 分钟），建议拆分步骤或并行化处理以缩短执行时间`);
  }

  if (sop.status === "published" && ec === 0) {
    suggestions.push("已发布但尚未被执行，建议组织培训推广并在团队内宣贯");
  }

  if (sr >= 95 && ec >= 10) {
    suggestions.push("执行稳定且次数充足，可作为标杆SOP推广到其他部门或同类场景");
  }

  if (vn > 5) {
    suggestions.push(`版本迭代频繁（共 ${vn} 个版本），建议固化流程并减少变更频率`);
  }

  // 用户自定义建议
  if (sop.optimizationSuggestions) {
    for (const s of sop.optimizationSuggestions) {
      if (!suggestions.includes(s)) suggestions.push(s);
    }
  }

  return suggestions;
}

// ============================================
// 主组件
// ============================================

export default function SOPLibrary() {
  // SOP 列表
  const [sops, setSops] = useState<SOP[]>([]);
  // 选中 SOP 的 id
  const [selectedSopId, setSelectedSopId] = useState<string | null>(null);
  // 类别筛选
  const [activeCategory, setActiveCategory] = useState<"all" | SOPCategory>("all");
  // 搜索关键词
  const [searchQuery, setSearchQuery] = useState("");
  // 是否显示编辑 Modal
  const [showEditModal, setShowEditModal] = useState(false);
  // 编辑中的 SOP（null 表示新建）
  const [editingSop, setEditingSop] = useState<SOP | null>(null);
  // 是否显示 Obsidian 导入 Modal
  const [showObsidianModal, setShowObsidianModal] = useState(false);
  // 工作流模板选项（用于关联）
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowTemplateOption[]>([]);

  // 初次挂载：加载 SOP 与工作流模板
  useEffect(() => {
    setSops(loadSops());
    setWorkflowTemplates(loadWorkflowTemplateOptions());
  }, []);

  // SOP 变化时持久化
  useEffect(() => {
    saveSops(sops);
  }, [sops]);

  // 监听工作流模板存储变化（同标签页手动刷新时同步）
  useEffect(() => {
    const handler = () => setWorkflowTemplates(loadWorkflowTemplateOptions());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // 当前选中的 SOP 对象
  const selectedSop = useMemo(
    () => sops.find((s) => s.id === selectedSopId) ?? null,
    [sops, selectedSopId]
  );

  // 按类别 + 关键词筛选
  const filteredSops = useMemo(() => {
    const kw = searchQuery.trim().toLowerCase();
    return sops.filter((s) => {
      if (activeCategory !== "all" && s.category !== activeCategory) return false;
      if (!kw) return true;
      return (
        s.title.toLowerCase().includes(kw) ||
        s.department.toLowerCase().includes(kw) ||
        s.content.toLowerCase().includes(kw)
      );
    });
  }, [sops, activeCategory, searchQuery]);

  // 顶部统计
  const stats = useMemo(() => {
    const total = sops.length;
    const published = sops.filter((s) => s.status === "published").length;
    const totalExec = sops.reduce((sum, s) => sum + (s.executionCount ?? 0), 0);
    const withExec = sops.filter((s) => (s.executionCount ?? 0) > 0);
    const avgSuccess =
      withExec.length > 0
        ? withExec.reduce((sum, s) => sum + (s.successRate ?? 0), 0) / withExec.length
        : 0;
    return { total, published, totalExec, avgSuccess };
  }, [sops]);

  // ============================================
  // 新建 SOP：打开编辑 Modal
  // ============================================
  const handleCreate = useCallback(() => {
    setEditingSop(null);
    setShowEditModal(true);
  }, []);

  // ============================================
  // 编辑 SOP：打开编辑 Modal
  // ============================================
  const handleEdit = useCallback((sop: SOP) => {
    setEditingSop(sop);
    setShowEditModal(true);
  }, []);

  // ============================================
  // 保存 SOP（新建或更新，更新时创建新版本）
  // ============================================
  const handleSaveSop = useCallback(
    (draft: SOP, changelog: string, asNewVersion: boolean) => {
      const now = new Date().toISOString();
      if (editingSop) {
        // 编辑模式：更新字段
        const versions = asNewVersion
          ? [
              ...editingSop.versions,
              {
                version: bumpVersion(editingSop.versions.at(-1)?.version),
                content: draft.content,
                changelog: changelog || "更新SOP内容",
                updatedAt: now,
                updatedBy: "当前用户",
              },
            ]
          : editingSop.versions;

        const updated: SOP = {
          ...draft,
          id: editingSop.id,
          versions,
          createdAt: editingSop.createdAt,
          updatedAt: now,
        };
        // 自动生成优化建议（合并用户已填的）
        updated.optimizationSuggestions = generateOptimizationSuggestions(updated);
        setSops((prev) => prev.map((s) => (s.id === editingSop.id ? updated : s)));
      } else {
        // 新建模式：创建首个版本
        const newSop: SOP = {
          ...draft,
          id: genId("sop"),
          versions: [
            {
              version: "1.0.0",
              content: draft.content,
              changelog: changelog || "初始版本",
              updatedAt: now,
              updatedBy: "当前用户",
            },
          ],
          executionCount: 0,
          successRate: 0,
          avgDuration: 0,
          createdAt: now,
          updatedAt: now,
        };
        newSop.optimizationSuggestions = generateOptimizationSuggestions(newSop);
        setSops((prev) => [newSop, ...prev]);
        setSelectedSopId(newSop.id);
      }
      setShowEditModal(false);
      setEditingSop(null);
    },
    [editingSop]
  );

  // ============================================
  // 删除 SOP
  // ============================================
  const handleDelete = useCallback(
    (sopId: string) => {
      setSops((prev) => prev.filter((s) => s.id !== sopId));
      if (selectedSopId === sopId) setSelectedSopId(null);
    },
    [selectedSopId]
  );

  // ============================================
  // 回滚到指定历史版本（创建一个新版本，内容为历史版本）
  // ============================================
  const handleRollback = useCallback(
    (sopId: string, versionIdx: number) => {
      setSops((prev) =>
        prev.map((s) => {
          if (s.id !== sopId) return s;
          const target = s.versions[versionIdx];
          if (!target) return s;
          const now = new Date().toISOString();
          const newVersion: SOPVersion = {
            version: bumpVersion(s.versions.at(-1)?.version),
            content: target.content,
            changelog: `回滚至 v${target.version}`,
            updatedAt: now,
            updatedBy: "当前用户",
          };
          const updated: SOP = {
            ...s,
            content: target.content,
            versions: [...s.versions, newVersion],
            updatedAt: now,
          };
          updated.optimizationSuggestions = generateOptimizationSuggestions(updated);
          return updated;
        })
      );
    },
    []
  );

  // ============================================
  // 关联 / 取消关联工作流模板
  // ============================================
  const handleLinkWorkflow = useCallback((sopId: string, templateId: string | undefined) => {
    setSops((prev) =>
      prev.map((s) =>
        s.id === sopId
          ? { ...s, linkedWorkflowTemplateId: templateId, updatedAt: new Date().toISOString() }
          : s
      )
    );
  }, []);

  // ============================================
  // 更新培训考核信息
  // ============================================
  const handleUpdateTraining = useCallback(
    (
      sopId: string,
      data: {
        trainingMaterials?: string[];
        assessmentQuestions?: { question: string; answer: string }[];
        executionCount?: number;
        successRate?: number;
        avgDuration?: number;
      }
    ) => {
      setSops((prev) =>
        prev.map((s) => {
          if (s.id !== sopId) return s;
          const updated: SOP = {
            ...s,
            trainingMaterials: data.trainingMaterials ?? s.trainingMaterials,
            assessmentQuestions: data.assessmentQuestions ?? s.assessmentQuestions,
            executionCount: data.executionCount ?? s.executionCount,
            successRate: data.successRate ?? s.successRate,
            avgDuration: data.avgDuration ?? s.avgDuration,
            updatedAt: new Date().toISOString(),
          };
          updated.optimizationSuggestions = generateOptimizationSuggestions(updated);
          return updated;
        })
      );
    },
    []
  );

  // ============================================
  // 从 Obsidian 导入（创建新 SOP）
  // ============================================
  const handleImportFromObsidian = useCallback((item: ObsidianItem) => {
    const now = new Date().toISOString();
    const newSop: SOP = {
      id: genId("sop"),
      title: item.title || "从 Obsidian 导入的 SOP",
      category: "operations",
      department: "未指定",
      status: "draft",
      content: item.content || "",
      versions: [
        {
          version: "1.0.0",
          content: item.content || "",
          changelog: `从 Obsidian 导入${item.path ? `（${item.path}）` : ""}`,
          updatedAt: now,
          updatedBy: "当前用户",
        },
      ],
      executionCount: 0,
      successRate: 0,
      avgDuration: 0,
      createdAt: now,
      updatedAt: now,
    };
    newSop.optimizationSuggestions = generateOptimizationSuggestions(newSop);
    setSops((prev) => [newSop, ...prev]);
    setSelectedSopId(newSop.id);
    setShowObsidianModal(false);
  }, []);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-sky-500/15 border border-sky-500/30">
            <BookOpen size={13} className="text-sky-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              企业SOP库
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              SOP Library · 共 {stats.total} · 已发布 {stats.published}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowObsidianModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors"
          >
            <Database size={11} className="text-violet-400" />
            <span className="text-[9px] font-mono text-violet-300 uppercase tracking-wider">
              Obsidian 导入
            </span>
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
          >
            <Plus size={11} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              创建SOP
            </span>
          </button>
          <button
            onClick={() => {
              setSops(loadSops());
              setWorkflowTemplates(loadWorkflowTemplateOptions());
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/30 hover:bg-sky-500/25 transition-colors"
          >
            <RefreshCw size={11} className="text-sky-400" />
            <span className="text-[9px] font-mono text-sky-300 uppercase tracking-wider">
              刷新
            </span>
          </button>
        </div>
      </div>

      {/* 顶部统计卡片 */}
      <div className="p-4 border-b border-white/[0.04] grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <StatCard
          icon={BookOpen}
          label="SOP 总数"
          value={stats.total.toString()}
          color="#38BDF8"
        />
        <StatCard
          icon={CheckCircle2}
          label="已发布"
          value={stats.published.toString()}
          color="#34D399"
        />
        <StatCard
          icon={Activity}
          label="累计执行次数"
          value={stats.totalExec.toString()}
          color="#FBBF24"
        />
        <StatCard
          icon={TrendingUp}
          label="平均成功率"
          value={`${stats.avgSuccess.toFixed(1)}%`}
          color="#A78BFA"
        />
      </div>

      {/* 类别筛选 + 搜索框 */}
      <div className="p-4 border-b border-white/[0.04] space-y-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Filter size={10} className="text-gray-500 flex-shrink-0" />
          {CATEGORY_FILTERS.map((f) => {
            const isActive = activeCategory === f.id;
            const count =
              f.id === "all"
                ? sops.length
                : sops.filter((s) => s.category === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setActiveCategory(f.id)}
                className={`px-2 py-1 rounded-md text-[10px] font-mono transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                    : "text-gray-500 hover:text-gray-300 border border-transparent"
                }`}
              >
                {f.label}
                <span
                  className={`px-1 rounded text-[8px] ${
                    isActive ? "bg-sky-500/20 text-sky-300" : "bg-white/[0.05] text-gray-600"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索 SOP 标题 / 部门 / 内容"
            className="w-full pl-7 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-sky-500/40"
          />
        </div>
      </div>

      {/* 中部：左 SOP 列表 + 右详情 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* 左：SOP 列表 */}
        <div className="p-4 border-r border-white/[0.04] max-h-[640px] overflow-y-auto">
          {filteredSops.length === 0 ? (
            <div className="py-12 text-center">
              <BookOpen size={24} className="mx-auto text-gray-600 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">暂无SOP</p>
              <p className="text-[9px] font-mono text-gray-700 mt-1">点击"创建SOP"或从 Obsidian 导入</p>
              <button
                onClick={handleCreate}
                className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors text-[10px] font-mono uppercase tracking-wider"
              >
                <Plus size={10} />
                点击创建
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSops.map((sop) => (
                <SOPCard
                  key={sop.id}
                  sop={sop}
                  selected={selectedSopId === sop.id}
                  workflowName={
                    sop.linkedWorkflowTemplateId
                      ? workflowTemplates.find((t) => t.id === sop.linkedWorkflowTemplateId)?.name
                      : undefined
                  }
                  onSelect={() => setSelectedSopId(sop.id)}
                  onEdit={() => handleEdit(sop)}
                  onDelete={() => handleDelete(sop.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* 右：SOP 详情 */}
        <div className="p-4 max-h-[640px] overflow-y-auto">
          {selectedSop ? (
            <SOPDetail
              sop={selectedSop}
              workflowTemplates={workflowTemplates}
              onEdit={() => handleEdit(selectedSop)}
              onDelete={() => handleDelete(selectedSop.id)}
              onRollback={(idx) => handleRollback(selectedSop.id, idx)}
              onLinkWorkflow={(tplId) => handleLinkWorkflow(selectedSop.id, tplId)}
              onUpdateTraining={(data) => handleUpdateTraining(selectedSop.id, data)}
            />
          ) : (
            <div className="py-12 text-center">
              <FileText size={24} className="mx-auto text-gray-600 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">选择左侧 SOP 查看详情</p>
              <p className="text-[9px] font-mono text-gray-700 mt-1">
                版本历史 / 执行统计 / 优化建议 / 培训考核 / 关联工作流
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 编辑 Modal */}
      <AnimatePresence>
        {showEditModal && (
          <SOPEditModal
            sop={editingSop}
            onClose={() => {
              setShowEditModal(false);
              setEditingSop(null);
            }}
            onSave={handleSaveSop}
          />
        )}
      </AnimatePresence>

      {/* Obsidian 导入 Modal */}
      <AnimatePresence>
        {showObsidianModal && (
          <ObsidianImportModal
            onClose={() => setShowObsidianModal(false)}
            onImport={handleImportFromObsidian}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：统计卡片
// ============================================
interface StatCardProps {
  icon: typeof BookOpen;
  label: string;
  value: string;
  color: string;
}

function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 flex items-center gap-2">
      <div
        className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 border"
        style={{ background: `${color}15`, borderColor: `${color}30` }}
      >
        <Icon size={14} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="text-[16px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 子组件：SOP 卡片
// ============================================
interface SOPCardProps {
  sop: SOP;
  selected: boolean;
  workflowName?: string;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function SOPCard({
  sop,
  selected,
  workflowName,
  onSelect,
  onEdit,
  onDelete,
}: SOPCardProps) {
  const catMeta = CATEGORY_META[sop.category];
  const CatIcon = catMeta.icon;
  const statusMeta = STATUS_META[sop.status];
  const versionCount = sop.versions.length;
  const hasOptIssues = (sop.optimizationSuggestions?.length ?? 0) > 0;
  const sr = sop.successRate ?? 0;
  const srColor = sr >= 95 ? "#34D399" : sr >= 80 ? "#FBBF24" : sr > 0 ? "#F87171" : "#64748B";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      onClick={onSelect}
      className={`group relative rounded-lg border bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-3 cursor-pointer ${
        selected
          ? "border-sky-500/40 bg-sky-500/[0.04]"
          : "border-white/[0.06] hover:border-white/[0.1]"
      }`}
      style={{ borderLeft: `2px solid ${catMeta.color}` }}
    >
      {/* 卡片头部：图标 + 标题 + 状态 */}
      <div className="flex items-start gap-2">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 border"
          style={{ background: `${catMeta.color}15`, borderColor: `${catMeta.color}30` }}
        >
          <CatIcon size={13} style={{ color: catMeta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-gray-100 truncate">
              {sop.title}
            </span>
            <span
              className="px-1 rounded text-[8px] font-mono flex-shrink-0"
              style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}
            >
              {statusMeta.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span
              className="px-1 rounded text-[8px] font-mono"
              style={{ background: `${catMeta.color}20`, color: catMeta.color }}
            >
              {catMeta.label}
            </span>
            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
              <GitBranch size={8} />
              v{sop.versions.at(-1)?.version ?? "1.0.0"}
            </span>
            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
              <History size={8} />
              {versionCount}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            title="编辑"
            className="p-1 rounded text-gray-600 hover:text-sky-400 hover:bg-sky-500/10 transition-colors"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="删除"
            className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {/* 部门 + 执行统计标签 */}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500">
          <Building2 size={9} />
          {sop.department || "未指定"}
        </span>
        <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500">
          <Activity size={9} />
          执行 {sop.executionCount}
        </span>
        <span className="flex items-center gap-0.5 text-[9px] font-mono" style={{ color: srColor }}>
          <Target size={9} />
          {sop.executionCount > 0 ? `${sr.toFixed(0)}%` : "—"}
        </span>
        {sop.avgDuration > 0 && (
          <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500">
            <Clock size={9} />
            {sop.avgDuration.toFixed(0)}min
          </span>
        )}
        {workflowName && (
          <span className="flex items-center gap-0.5 text-[9px] font-mono text-sky-400">
            <Link2 size={9} />
            {workflowName}
          </span>
        )}
        {hasOptIssues && (
          <span className="flex items-center gap-0.5 text-[9px] font-mono text-amber-400">
            <Lightbulb size={9} />
            优化建议
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：SOP 详情面板
// ============================================
interface SOPDetailProps {
  sop: SOP;
  workflowTemplates: WorkflowTemplateOption[];
  onEdit: () => void;
  onDelete: () => void;
  onRollback: (versionIdx: number) => void;
  onLinkWorkflow: (templateId: string | undefined) => void;
  onUpdateTraining: (data: {
    trainingMaterials?: string[];
    assessmentQuestions?: { question: string; answer: string }[];
    executionCount?: number;
    successRate?: number;
    avgDuration?: number;
  }) => void;
}

type DetailTab = "content" | "versions" | "stats" | "suggestions" | "training" | "workflow";

function SOPDetail({
  sop,
  workflowTemplates,
  onEdit,
  onDelete,
  onRollback,
  onLinkWorkflow,
  onUpdateTraining,
}: SOPDetailProps) {
  const [tab, setTab] = useState<DetailTab>("content");
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);

  const catMeta = CATEGORY_META[sop.category];
  const CatIcon = catMeta.icon;
  const statusMeta = STATUS_META[sop.status];
  const suggestions = useMemo(() => generateOptimizationSuggestions(sop), [sop]);

  const linkedTemplate = sop.linkedWorkflowTemplateId
    ? workflowTemplates.find((t) => t.id === sop.linkedWorkflowTemplateId)
    : undefined;

  const tabs: { id: DetailTab; label: string; icon: typeof FileText }[] = [
    { id: "content", label: "内容", icon: FileText },
    { id: "versions", label: `版本(${sop.versions.length})`, icon: History },
    { id: "stats", label: "执行统计", icon: TrendingUp },
    { id: "suggestions", label: `建议(${suggestions.length})`, icon: Lightbulb },
    { id: "training", label: "培训考核", icon: GraduationCap },
    { id: "workflow", label: "关联工作流", icon: Link2 },
  ];

  return (
    <div className="space-y-3">
      {/* 详情头部 */}
      <div className="flex items-start gap-2.5">
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 border"
          style={{ background: `${catMeta.color}15`, borderColor: `${catMeta.color}30` }}
        >
          <CatIcon size={15} style={{ color: catMeta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-semibold text-white truncate">{sop.title}</span>
            <span
              className="px-1 rounded text-[8px] font-mono flex-shrink-0"
              style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}
            >
              {statusMeta.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-500 uppercase tracking-wider flex-wrap">
            <span style={{ color: catMeta.color }}>{catMeta.label}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <Building2 size={8} />
              {sop.department || "未指定"}
            </span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <GitBranch size={8} />v{sop.versions.at(-1)?.version ?? "1.0.0"}
            </span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <Clock size={8} />
              {formatDateTime(sop.updatedAt)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            title="编辑"
            className="p-1.5 rounded-md text-gray-400 hover:text-sky-400 hover:bg-sky-500/10 transition-colors"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onDelete}
            title="删除"
            className="p-1.5 rounded-md text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex items-center gap-1 flex-wrap border-b border-white/[0.04] pb-2">
        {tabs.map((t) => {
          const isActive = tab === t.id;
          const TabIcon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono transition-colors ${
                isActive
                  ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                  : "text-gray-500 hover:text-gray-300 border border-transparent"
              }`}
            >
              <TabIcon size={10} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab 内容 */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15 }}
        >
          {/* 内容预览 */}
          {tab === "content" && (
            <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-3 max-h-[420px] overflow-y-auto">
              {sop.content.trim() ? (
                <SimpleMarkdownRenderer content={sop.content} />
              ) : (
                <p className="text-[10px] font-mono text-gray-600 text-center py-4">
                  暂无内容，点击编辑添加
                </p>
              )}
            </div>
          )}

          {/* 版本历史 */}
          {tab === "versions" && (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {sop.versions.length === 0 ? (
                <p className="text-[10px] font-mono text-gray-600 text-center py-4">暂无版本记录</p>
              ) : (
                [...sop.versions].reverse().map((v, idx) => {
                  const realIdx = sop.versions.length - 1 - idx;
                  const isLatest = realIdx === sop.versions.length - 1;
                  return (
                    <div
                      key={`${v.version}-${realIdx}`}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5"
                    >
                      <div className="flex items-center gap-1.5">
                        <GitBranch size={11} className="text-sky-400 flex-shrink-0" />
                        <span className="text-[11px] font-mono font-semibold text-gray-200">
                          v{v.version}
                        </span>
                        {isLatest && (
                          <span className="px-1 rounded text-[8px] font-mono bg-emerald-500/15 text-emerald-300">
                            当前
                          </span>
                        )}
                        <span className="text-[9px] font-mono text-gray-600 ml-auto">
                          {formatDateTime(v.updatedAt)}
                        </span>
                        {!isLatest && (
                          <button
                            onClick={() => onRollback(realIdx)}
                            title="回滚到此版本"
                            className="p-1 rounded text-gray-600 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                          >
                            <RotateCcw size={10} />
                          </button>
                        )}
                      </div>
                      <div className="text-[9px] font-mono text-gray-500 mt-1">
                        更新人：{v.updatedBy}
                      </div>
                      <p className="text-[10px] font-mono text-gray-400 mt-1 leading-relaxed">
                        {v.changelog || "无变更说明"}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* 执行统计 */}
          {tab === "stats" && (
            <div className="space-y-2.5">
              <div className="grid grid-cols-3 gap-2">
                <StatCard
                  icon={Activity}
                  label="执行次数"
                  value={sop.executionCount.toString()}
                  color="#38BDF8"
                />
                <StatCard
                  icon={Target}
                  label="成功率"
                  value={`${sop.successRate.toFixed(0)}%`}
                  color={sop.successRate >= 80 ? "#34D399" : "#F87171"}
                />
                <StatCard
                  icon={Clock}
                  label="平均耗时"
                  value={`${sop.avgDuration.toFixed(0)}min`}
                  color="#FBBF24"
                />
              </div>
              <button
                onClick={() => setShowStatsModal(true)}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
              >
                <Pencil size={11} />
                录入执行数据
              </button>
              <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                <SectionTitle icon={TrendingUp} label="执行趋势解读" color="#A78BFA" />
                <p className="text-[10px] font-mono text-gray-400 mt-1.5 leading-relaxed">
                  {sop.executionCount === 0
                    ? "该SOP尚未被执行，无统计数据。发布并推广后可录入执行数据以追踪效果。"
                    : `已执行 ${sop.executionCount} 次，成功率 ${sop.successRate.toFixed(0)}%，平均耗时 ${sop.avgDuration.toFixed(0)} 分钟。${
                        sop.successRate >= 95
                          ? "执行稳定，表现优秀。"
                          : sop.successRate >= 80
                          ? "执行良好，仍有优化空间。"
                          : "成功率偏低，需重点优化。"
                      }`}
                </p>
              </div>
            </div>
          )}

          {/* 优化建议 */}
          {tab === "suggestions" && (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {suggestions.length === 0 ? (
                <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-3 text-center">
                  <CheckCircle2 size={16} className="mx-auto text-emerald-400 mb-1" />
                  <p className="text-[10px] font-mono text-gray-400">
                    暂无优化建议，当前执行指标良好
                  </p>
                </div>
              ) : (
                suggestions.map((s, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-1.5 rounded-md border border-amber-500/15 bg-amber-500/[0.04] p-2.5"
                  >
                    <Lightbulb size={11} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[10px] font-mono text-amber-200/90 leading-relaxed">
                      {s}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* 培训考核 */}
          {tab === "training" && (
            <div className="space-y-2.5">
              <button
                onClick={() => setShowTrainingModal(true)}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
              >
                <Pencil size={11} />
                编辑培训考核
              </button>

              {/* 培训材料 */}
              <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                <SectionTitle
                  icon={FileText}
                  label={`培训材料（${sop.trainingMaterials?.length ?? 0}）`}
                  color="#38BDF8"
                />
                {(sop.trainingMaterials?.length ?? 0) === 0 ? (
                  <p className="text-[10px] font-mono text-gray-600 mt-1.5">暂无培训材料</p>
                ) : (
                  <ul className="mt-1.5 space-y-1">
                    {sop.trainingMaterials!.map((m, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-1.5 text-[10px] font-mono text-gray-300"
                      >
                        <FileText size={9} className="text-sky-400 flex-shrink-0 mt-0.5" />
                        <span className="break-all">{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* 考核题目 */}
              <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                <SectionTitle
                  icon={ClipboardList}
                  label={`考核题目（${sop.assessmentQuestions?.length ?? 0}）`}
                  color="#A78BFA"
                />
                {(sop.assessmentQuestions?.length ?? 0) === 0 ? (
                  <p className="text-[10px] font-mono text-gray-600 mt-1.5">暂无考核题目</p>
                ) : (
                  <ol className="mt-1.5 space-y-1.5">
                    {sop.assessmentQuestions!.map((q, idx) => (
                      <li
                        key={idx}
                        className="text-[10px] font-mono text-gray-300 leading-relaxed"
                      >
                        <div className="text-gray-200">
                          {idx + 1}. {q.question}
                        </div>
                        <div className="text-emerald-300/80 mt-0.5 pl-3">→ {q.answer}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}

          {/* 关联工作流 */}
          {tab === "workflow" && (
            <div className="space-y-2">
              <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                <SectionTitle icon={Link2} label="关联的工作流模板" color="#38BDF8" />
                {linkedTemplate ? (
                  <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-sky-500/20 bg-sky-500/[0.05]">
                    <Link2 size={11} className="text-sky-400 flex-shrink-0" />
                    <span className="text-[11px] font-mono text-sky-200 flex-1 truncate">
                      {linkedTemplate.name}
                    </span>
                    <span className="text-[8px] font-mono text-gray-500">{linkedTemplate.id}</span>
                    <button
                      onClick={() => onLinkWorkflow(undefined)}
                      title="取消关联"
                      className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Unlink size={11} />
                    </button>
                  </div>
                ) : (
                  <p className="text-[10px] font-mono text-gray-600 mt-1.5">
                    未关联工作流模板。可在下方选择一个模板进行关联，关联后该SOP可由对应工作流自动执行。
                  </p>
                )}
              </div>

              {/* 模板选择列表 */}
              <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                <SectionTitle icon={GitBranch} label="可选模板" color="#A78BFA" />
                {workflowTemplates.length === 0 ? (
                  <p className="text-[10px] font-mono text-gray-600 mt-1.5">
                    暂无可用工作流模板。请先在"工作流模板库"中创建模板。
                  </p>
                ) : (
                  <div className="mt-1.5 space-y-1 max-h-[260px] overflow-y-auto">
                    {workflowTemplates.map((t) => {
                      const isLinked = sop.linkedWorkflowTemplateId === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => onLinkWorkflow(isLinked ? undefined : t.id)}
                          className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-left transition-colors ${
                            isLinked
                              ? "border-sky-500/30 bg-sky-500/10"
                              : "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05]"
                          }`}
                        >
                          <Link2
                            size={11}
                            className={isLinked ? "text-sky-400" : "text-gray-500"}
                          />
                          <span
                            className={`text-[11px] font-mono flex-1 truncate ${
                              isLinked ? "text-sky-200" : "text-gray-300"
                            }`}
                          >
                            {t.name}
                          </span>
                          {isLinked && (
                            <CheckCircle2 size={11} className="text-sky-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* 执行数据录入 Modal */}
      <AnimatePresence>
        {showStatsModal && (
          <StatsEditModal
            executionCount={sop.executionCount}
            successRate={sop.successRate}
            avgDuration={sop.avgDuration}
            onClose={() => setShowStatsModal(false)}
            onSave={(data) => {
              onUpdateTraining(data);
              setShowStatsModal(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* 培训考核编辑 Modal */}
      <AnimatePresence>
        {showTrainingModal && (
          <TrainingEditModal
            trainingMaterials={sop.trainingMaterials ?? []}
            assessmentQuestions={sop.assessmentQuestions ?? []}
            onClose={() => setShowTrainingModal(false)}
            onSave={(data) => {
              onUpdateTraining(data);
              setShowTrainingModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：SOP 编辑 Modal（Markdown 编辑 + 预览 + 版本管理）
// ============================================
interface SOPEditModalProps {
  sop: SOP | null;
  onClose: () => void;
  onSave: (draft: SOP, changelog: string, asNewVersion: boolean) => void;
}

function SOPEditModal({ sop, onClose, onSave }: SOPEditModalProps) {
  const isEdit = !!sop;
  const [title, setTitle] = useState(sop?.title ?? "");
  const [category, setCategory] = useState<SOPCategory>(sop?.category ?? "operations");
  const [department, setDepartment] = useState(sop?.department ?? "");
  const [status, setStatus] = useState<SOPStatus>(sop?.status ?? "draft");
  const [content, setContent] = useState(sop?.content ?? "");
  const [changelog, setChangelog] = useState("");
  const [asNewVersion, setAsNewVersion] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  /** 提交保存 */
  const handleSubmit = () => {
    if (!title.trim()) return;
    const draft: SOP = {
      id: sop?.id ?? "",
      title: title.trim(),
      category,
      department: department.trim() || "未指定",
      status,
      content,
      versions: sop?.versions ?? [],
      executionCount: sop?.executionCount ?? 0,
      successRate: sop?.successRate ?? 0,
      avgDuration: sop?.avgDuration ?? 0,
      linkedWorkflowTemplateId: sop?.linkedWorkflowTemplateId,
      optimizationSuggestions: sop?.optimizationSuggestions,
      trainingMaterials: sop?.trainingMaterials,
      assessmentQuestions: sop?.assessmentQuestions,
      createdAt: sop?.createdAt ?? "",
      updatedAt: sop?.updatedAt ?? "",
    };
    onSave(draft, changelog.trim(), isEdit ? asNewVersion : true);
  };

  const canSubmit = title.trim().length > 0;

  /** 插入 Markdown 模板 */
  const insertTemplate = () => {
    const template = `## 适用范围\n\n描述该SOP适用的部门/场景。\n\n## 流程步骤\n\n1. 步骤一：\n2. 步骤二：\n3. 步骤三：\n\n## 注意事项\n\n- 关键风险点\n- 合规要求\n\n## 预期结果\n\n执行后应达到的效果。`;
    setContent((prev) => (prev.trim() ? `${prev}\n\n${template}` : template));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Pencil size={14} className="text-emerald-400" />
            <span className="text-[13px] font-semibold text-white">
              {isEdit ? "编辑SOP" : "创建SOP"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/[0.05] transition-colors"
          >
            <X size={14} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* 标题 + 部门 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                SOP 标题 *
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="如：客户投诉处理流程"
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                所属部门
              </label>
              <input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="如：客户成功部"
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>

          {/* 类别 + 状态 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                类别
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as SOPCategory)}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id} className="bg-[#131826]">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                状态
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as SOPStatus)}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id} className="bg-[#131826]">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 内容编辑 + 预览切换 */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                SOP 内容（Markdown）
              </label>
              <div className="flex items-center gap-1">
                <button
                  onClick={insertTemplate}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
                >
                  <Plus size={9} />
                  插入模板
                </button>
                <button
                  onClick={() => setShowPreview((p) => !p)}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                    showPreview
                      ? "bg-violet-500/15 border-violet-500/30 text-violet-300"
                      : "bg-white/[0.04] border-white/[0.06] text-gray-300 hover:bg-white/[0.08]"
                  }`}
                >
                  <Eye size={9} />
                  {showPreview ? "编辑" : "预览"}
                </button>
              </div>
            </div>
            {showPreview ? (
              <div className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] min-h-[180px] max-h-[280px] overflow-y-auto">
                {content.trim() ? (
                  <SimpleMarkdownRenderer content={content} />
                ) : (
                  <p className="text-[10px] font-mono text-gray-600 text-center py-4">暂无内容</p>
                )}
              </div>
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={"## 流程步骤\n\n1. 第一步...\n2. 第二步..."}
                rows={10}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40 resize-none"
              />
            )}
          </div>

          {/* 版本管理（仅编辑模式） */}
          {isEdit && (
            <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5 space-y-2">
              <div className="flex items-center gap-1.5">
                <GitBranch size={11} className="text-sky-400" />
                <span className="text-[10px] font-mono text-gray-300">版本管理</span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">
                  当前 v{sop.versions.at(-1)?.version ?? "1.0.0"}
                </span>
              </div>
              <label className="flex items-center gap-1.5 text-[10px] font-mono text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={asNewVersion}
                  onChange={(e) => setAsNewVersion(e.target.checked)}
                  className="accent-sky-500"
                />
                保存为新版本（保留历史版本，可回滚）
              </label>
              {asNewVersion && (
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                    变更说明（Changelog）
                  </label>
                  <input
                    value={changelog}
                    onChange={(e) => setChangelog(e.target.value)}
                    placeholder="如：优化步骤三，新增合规检查"
                    className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} />
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={12} />
            {isEdit ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：Obsidian 导入 Modal
// ============================================
interface ObsidianImportModalProps {
  onClose: () => void;
  onImport: (item: ObsidianItem) => void;
}

function ObsidianImportModal({ onClose, onImport }: ObsidianImportModalProps) {
  const [query, setQuery] = useState("SOP");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ObsidianItem[]>([]);
  const [searched, setSearched] = useState(false);

  /** 等待 RuntimeManager 初始化 */
  const waitForRuntime = useCallback(
    (timeoutMs = 9000) => RuntimeManager.waitForRuntime({ timeoutMs, intervalMs: 300 }),
    []
  );

  /** 执行检索：调用 KnowledgeRuntime 搜索 Obsidian 知识库 */
  const handleSearch = useCallback(async () => {
    const kw = query.trim();
    if (!kw) return;
    setLoading(true);
    setError(null);
    setResults([]);
    setSearched(false);
    try {
      const ready = await waitForRuntime();
      if (!ready) {
        setError("RuntimeManager 尚未初始化");
        setSearched(true);
        return;
      }
      if (!RuntimeManager.hasRuntime(RuntimeName.Knowledge)) {
        setError("知识库运行时未注册");
        setSearched(true);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
        type: "search",
        query: kw,
      });
      if (!resp?.success) {
        setError(resp?.error || "检索失败");
        setSearched(true);
        return;
      }
      // 兼容多种返回结构：data 可能是数组、{ results: [] }、{ items: [] } 等
      const data: any = resp.data;
      const items: ObsidianItem[] = [];
      const push = (it: any) => {
        if (!it) return;
        const title = String(it.title ?? it.name ?? it.path ?? "未命名文档");
        const content = String(it.content ?? it.text ?? it.body ?? it.summary ?? "");
        const path = it.path ? String(it.path) : undefined;
        items.push({ title, content, path });
      };
      if (Array.isArray(data)) {
        data.forEach(push);
      } else if (data && typeof data === "object") {
        const arr = data.results ?? data.items ?? data.documents ?? data.notes ?? data.list;
        if (Array.isArray(arr)) {
          arr.forEach(push);
        } else if (data.title || data.content) {
          push(data);
        }
      }
      setResults(items);
      setSearched(true);
    } catch (err) {
      setError((err as Error).message || "检索异常");
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [query, waitForRuntime]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Database size={14} className="text-violet-400" />
            <span className="text-[13px] font-semibold text-white">从 Obsidian 导入 SOP</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/[0.05] transition-colors"
          >
            <X size={14} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* 搜索框 */}
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleSearch();
                }}
                placeholder="输入关键词检索 Obsidian 知识库中的 SOP 文档"
                className="w-full pl-7 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-violet-500/40"
              />
            </div>
            <button
              onClick={() => void handleSearch()}
              disabled={loading || !query.trim()}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              检索
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] font-mono bg-red-500/10 text-red-300 border border-red-500/20">
              <AlertTriangle size={11} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* 检索结果 */}
          {searched && !loading && results.length === 0 && !error && (
            <div className="py-8 text-center">
              <Database size={22} className="mx-auto text-gray-600 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">未检索到匹配的 SOP 文档</p>
              <p className="text-[9px] font-mono text-gray-700 mt-1">尝试更换关键词</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                检索结果（{results.length}）· 点击导入为草稿 SOP
              </p>
              {results.map((item, idx) => (
                <div
                  key={idx}
                  className="group rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-violet-500/30 transition-colors p-2.5"
                >
                  <div className="flex items-start gap-1.5">
                    <FileText size={12} className="text-violet-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-mono font-semibold text-gray-200 truncate">
                        {item.title}
                      </div>
                      {item.path && (
                        <div className="text-[9px] font-mono text-gray-600 truncate">
                          {item.path}
                        </div>
                      )}
                      {item.content && (
                        <p className="text-[10px] font-mono text-gray-400 mt-1 line-clamp-3 leading-relaxed">
                          {item.content}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onImport(item)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors flex-shrink-0"
                    >
                      <Sparkles size={10} />
                      导入
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 初始提示 */}
          {!searched && !loading && (
            <div className="py-8 text-center">
              <Database size={22} className="mx-auto text-gray-600 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">通过 KnowledgeRuntime 检索 Obsidian 知识库</p>
              <p className="text-[9px] font-mono text-gray-700 mt-1">输入关键词后点击"检索"</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} />
            关闭
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：执行数据录入 Modal
// ============================================
interface StatsEditModalProps {
  executionCount: number;
  successRate: number;
  avgDuration: number;
  onClose: () => void;
  onSave: (data: {
    executionCount: number;
    successRate: number;
    avgDuration: number;
  }) => void;
}

function StatsEditModal({
  executionCount,
  successRate,
  avgDuration,
  onClose,
  onSave,
}: StatsEditModalProps) {
  const [ec, setEc] = useState(executionCount.toString());
  const [sr, setSr] = useState(successRate.toString());
  const [ad, setAd] = useState(avgDuration.toString());

  const handleSave = () => {
    onSave({
      executionCount: Math.max(0, parseInt(ec, 10) || 0),
      successRate: Math.min(100, Math.max(0, parseFloat(sr) || 0)),
      avgDuration: Math.max(0, parseFloat(ad) || 0),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-sky-400" />
            <span className="text-[13px] font-semibold text-white">录入执行数据</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/[0.05] transition-colors">
            <X size={14} className="text-gray-400" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              执行次数
            </label>
            <input
              type="number"
              min={0}
              value={ec}
              onChange={(e) => setEc(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-sky-500/40"
            />
          </div>
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              成功率（0-100）
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={sr}
              onChange={(e) => setSr(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-sky-500/40"
            />
          </div>
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              平均耗时（分钟）
            </label>
            <input
              type="number"
              min={0}
              value={ad}
              onChange={(e) => setAd(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-sky-500/40"
            />
          </div>
        </div>
        <div className="p-4 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} />
            取消
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
          >
            <Save size={12} />
            保存
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：培训考核编辑 Modal
// ============================================
interface TrainingEditModalProps {
  trainingMaterials: string[];
  assessmentQuestions: { question: string; answer: string }[];
  onClose: () => void;
  onSave: (data: {
    trainingMaterials: string[];
    assessmentQuestions: { question: string; answer: string }[];
  }) => void;
}

function TrainingEditModal({
  trainingMaterials,
  assessmentQuestions,
  onClose,
  onSave,
}: TrainingEditModalProps) {
  const [materials, setMaterials] = useState<string[]>(
    trainingMaterials.length > 0 ? [...trainingMaterials] : [""]
  );
  const [questions, setQuestions] = useState<{ question: string; answer: string }[]>(
    assessmentQuestions.length > 0 ? assessmentQuestions.map((q) => ({ ...q })) : [{ question: "", answer: "" }]
  );

  const updateMaterial = (idx: number, val: string) => {
    setMaterials((prev) => prev.map((m, i) => (i === idx ? val : m)));
  };
  const addMaterial = () => setMaterials((prev) => [...prev, ""]);
  const removeMaterial = (idx: number) =>
    setMaterials((prev) => prev.filter((_, i) => i !== idx));

  const updateQuestion = (idx: number, patch: Partial<{ question: string; answer: string }>) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const addQuestion = () =>
    setQuestions((prev) => [...prev, { question: "", answer: "" }]);
  const removeQuestion = (idx: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    onSave({
      trainingMaterials: materials.map((m) => m.trim()).filter(Boolean),
      assessmentQuestions: questions
        .map((q) => ({ question: q.question.trim(), answer: q.answer.trim() }))
        .filter((q) => q.question),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <GraduationCap size={14} className="text-emerald-400" />
            <span className="text-[13px] font-semibold text-white">编辑培训考核</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/[0.05] transition-colors">
            <X size={14} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* 培训材料 */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                培训材料（每行一项，可为文档链接或说明）
              </label>
              <button
                onClick={addMaterial}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
              >
                <Plus size={9} />
                添加
              </button>
            </div>
            <div className="mt-1.5 space-y-1.5">
              {materials.map((m, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-600 w-4 flex-shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <input
                    value={m}
                    onChange={(e) => updateMaterial(idx, e.target.value)}
                    placeholder="如：新人培训手册 v1.2 / https://..."
                    className="flex-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                  />
                  {materials.length > 1 && (
                    <button
                      onClick={() => removeMaterial(idx)}
                      className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 考核题目 */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                考核题目（问答对）
              </label>
              <button
                onClick={addQuestion}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
              >
                <Plus size={9} />
                添加
              </button>
            </div>
            <div className="mt-1.5 space-y-2">
              {questions.map((q, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2 space-y-1.5"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono text-gray-600 w-4 flex-shrink-0">
                      Q{idx + 1}
                    </span>
                    <input
                      value={q.question}
                      onChange={(e) => updateQuestion(idx, { question: e.target.value })}
                      placeholder="问题"
                      className="flex-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                    />
                    {questions.length > 1 && (
                      <button
                        onClick={() => removeQuestion(idx)}
                        className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono text-gray-600 w-4 flex-shrink-0">A</span>
                    <input
                      value={q.answer}
                      onChange={(e) => updateQuestion(idx, { answer: e.target.value })}
                      placeholder="答案"
                      className="flex-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-emerald-300/90 font-mono focus:outline-none focus:border-emerald-500/40"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} />
            取消
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
          >
            <Save size={12} />
            保存
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：区块标题
// ============================================
function SectionTitle({
  icon: Icon,
  label,
  color,
}: {
  icon: typeof FileText;
  label: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={12} style={{ color }} />
      <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color }}>
        {label}
      </span>
    </div>
  );
}
